import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ checking: true, valid: false, email: "", name: "" });
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState({ checking: false, valid: false }); return; }
    api.get(`/auth/activation/valid?token=${encodeURIComponent(token)}`)
      .then((r) => setState({ checking: false, ...r.data }))
      .catch(() => setState({ checking: false, valid: false }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password: pw });
      setUser(data);
      toast.success("Welcome! You're all set.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not set password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16" data-testid="set-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-100 p-8">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
          <span className="font-heading text-xl text-ink">Iridescent Rewards</span>
        </div>

        {state.checking ? (
          <p className="text-sm text-[#8A9492]">Checking your link…</p>
        ) : !state.valid ? (
          <div data-testid="set-password-invalid">
            <h1 className="font-heading text-2xl text-ink mb-2">Link expired or invalid</h1>
            <p className="text-sm text-[#8A9492]">This activation link is no longer valid. Please ask the spa to resend your rewards invite, or <Link to="/login" className="text-iri-mid underline">log in</Link> if you already have a password.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="set-password-form">
            <h1 className="font-heading text-2xl text-ink mb-1">Set your password</h1>
            <p className="text-sm text-[#8A9492] mb-6">Welcome{state.name ? `, ${state.name.split(" ")[0]}` : ""}! Create a password for <strong>{state.email}</strong> to access your rewards.</p>
            <label className="text-xs text-[#8A9492]">New password</label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="mt-1 mb-4" data-testid="set-password-input" />
            <label className="text-xs text-[#8A9492]">Confirm password</label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="mt-1 mb-6" data-testid="set-password-confirm" />
            <Button type="submit" disabled={busy} data-testid="set-password-submit" className="w-full iri-gradient text-ink rounded-full h-12 font-medium">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set password & log in"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
